import React, { useLayoutEffect, useRef, useState } from "react";
import AgendaColumn from "./AgendaColumn";
import RdvInfoPopup from "./RdvInfoPopup";
import HolidayInfoPopup from "./HolidayInfoPopup";
import { DayRdvsProps } from "./DayRdvs";
import { processPopupCaracs } from "../filterTransforms/processPopupCaracs";
import { useSetAtom } from "jotai";
import { constrainedHeightAtom } from "../../main/state/globalState.ts";
import { AppointmentsId, PatientFlags, Timed } from "cadendar-shared";
import _ from "underts";
interface AgendaColMainCompProps<R extends { tag: string }> {
  weekRdvsArray: DayRdvsProps<R>["rdvs"][];
  holidayNames?: (string | null)[];
  canManageStaffAbsences?: boolean;
  dayAbsenceSummaries?: Array<
    Array<{
      absenceId: string;
      staffId: string;
      staffFullname: string;
      partOfDay: "full_day" | "morning" | "afternoon";
      leaveType: "paid" | "unpaid" | "special";
    }>
  >;
  débutHeureAffichage: number;
  finHeureAffichage: number;
  duréeMiniRdv: number;
  now: Date;
  onCaseClick: (
    rdv: R &
      (
        | { tag: "empty"; date: Date; hour: number; minute: number }
        | { tag: "rdv"; id: string }
      )
  ) => void;
  onCloseCrossClick: (id: string) => void;
  onConfirmClick: (id: string) => void;
  onDayHeaderClick?: (options: { event: React.MouseEvent<HTMLDivElement>; columnNumber: number }) => void;
}

const isHTMLElement = (target: unknown): target is HTMLElement => {
  return target instanceof HTMLElement;
};

const handleRdvCaseMouseEnter = (
  target: HTMLElement,
  rdvId: string,
  rdvs: {
    id: AppointmentsId;
    birthdate: string | null;
    name: string | null;
    forename: string | null;
    hour: number;
    minute: number;
    duration: number;
    object: string | null;
    addressed_by: string | null;
    patient_flags: PatientFlags | null;
  }[]
) => {
  const rdv = rdvs.find((rdv) => rdv.id === rdvId);
  if (!rdv) {
    return;
  }
  const caracs = {
    rdv,
    left: target.getBoundingClientRect().left,
    top: target.getBoundingClientRect().top + window.pageYOffset,
    height: target.offsetHeight,
    width: target.offsetWidth,
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    scrollY: window.scrollY,
  };
  if (
    typeof caracs.rdv === "object" &&
    caracs.rdv &&
    "id" in caracs.rdv &&
    "name" in caracs.rdv
  ) {
    return processPopupCaracs(caracs);
  }
};

const handleColumnHeaderMouseEnter = (
  event: React.MouseEvent<HTMLDivElement>,
  target: HTMLElement,
  column: number
) => {
  return {
    left: event.pageX,
    top: event.pageY,
    height: target.offsetHeight,
    width: target.offsetWidth,
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    scrollY: window.scrollY,
    column,
  };
};

const AgendaColMainComp = <R extends { tag: string }>(
  props: AgendaColMainCompProps<R>
) => {
  const [rdvInfoPopupCaracs, setRdvInfoPopupCaracs] = useState<{
    top: number;
    left: number;
    rdv: {
      id: AppointmentsId;
      birthdate: string | null;
      name: string | null;
      forename: string | null;
      hour: number;
      minute: number;
      duration: number;
      object: string | null;
      addressed_by: string | null;
      patient_flags: PatientFlags | null;
    };
  } | null>(null);
  const [columnInfoPopupCaracs, setColumnInfoPopupCaracs] = useState<ReturnType<
    typeof handleColumnHeaderMouseEnter
  > | null>(null);
  const lastConstrainedHeightRef = useRef<number | null>(null);

  const {
    weekRdvsArray,
    holidayNames = [],
    dayAbsenceSummaries = [],
    canManageStaffAbsences,
    onDayHeaderClick,
    ...rest
  } = props;
  const setConstrainedHeight = useSetAtom(constrainedHeightAtom);
  useLayoutEffect(() => {
    const heightHeader = document
      .querySelector(".headerCol")
      ?.getBoundingClientRect().height;
    const heightSpacer = document.querySelector(".spacerCol")?.clientHeight;
    if (heightHeader && heightSpacer) {
      const height = heightHeader + heightSpacer;
      if (lastConstrainedHeightRef.current !== height) {
        lastConstrainedHeightRef.current = height;
        setConstrainedHeight(height);
      }
    }
  });

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLDivElement>,
    rdvId: string | null,
    column?: number
  ) => {
    const target = event.currentTarget;
    if (!isHTMLElement(target)) {
      return;
    }
    if (rdvId) {
      const caracs = handleRdvCaseMouseEnter(
        target,
        rdvId,
        weekRdvsArray.flat().filter((rdv) => rdv.tag === "rdv")
      );
      if (caracs) {
        setRdvInfoPopupCaracs(caracs);
      }
      return;
    }
    if (column !== undefined) {
      const caracs = handleColumnHeaderMouseEnter(event, target, column);
      setColumnInfoPopupCaracs(caracs);
      return;
    }
  };

  const handleMouseLeave = () => {
    setRdvInfoPopupCaracs(null);
    setColumnInfoPopupCaracs(null);
  };

  const dayComps = weekRdvsArray.map((rdvs, idx) => (
    <AgendaColumn
      rdvs={rdvs}
      holidayName={holidayNames[idx] || null}
      dayAbsenceSummaries={dayAbsenceSummaries[idx] || []}
      canManageStaffAbsences={canManageStaffAbsences}
      columnNumber={idx}
      {...rest}
      onDayHeaderClick={onDayHeaderClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      key={"agendaColKey_" + idx}
    />
  ));

  const showRdvInfoPopup = !!rdvInfoPopupCaracs;
  const holidayName =
    columnInfoPopupCaracs && holidayNames[columnInfoPopupCaracs.column]
      ? holidayNames[columnInfoPopupCaracs.column]
      : null;
  return (
    <>
      {dayComps}
      {showRdvInfoPopup ? <RdvInfoPopup {...rdvInfoPopupCaracs} /> : null}
      {columnInfoPopupCaracs && holidayName ? (
        <HolidayInfoPopup
          top={columnInfoPopupCaracs.top + 16}
          left={columnInfoPopupCaracs.left + 12}
          holidayName={holidayName}
        />
      ) : null}
    </>
  );
};

export default AgendaColMainComp;
